import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, throwError } from 'rxjs';
import { PosSessionStatus, PosSessionsApiService } from 'api';
import { PosContextService } from '../services/pos-context.service';

export const POS_SUPERVISOR_REQUIRED$ = new Subject<boolean>();

/**
 * Pedidos de supervisor del backend (guía §3.7, BR-14/18/19/20/36): terminan en «Requiere supervisor.» o dicen
 * «requiere autorización de supervisor». Patrón estrecho a propósito: NO es `/supervisor/i`, que también
 * matchea el rechazo de la autorización («Supervisor o PIN incorrecto.», #998) y los tres textos que reemplazó.
 */
const SUPERVISOR_REQUIRED_TEXT = /requiere\s+(?:(?:una\s+)?autorizaci[oó]n\s+de\s+)?(?:un\s+)?supervisor/i;

/**
 * Heurística única para detectar el error de "requiere supervisor" tras la refactorización del backend:
 * llega como HTTP 400 con un mensaje que lo pide. Usada por el interceptor y por las páginas
 * (p. ej. `sale.page`) para no duplicar la lógica de string.
 *
 * Es el pedido de autorización, no su rechazo: «Supervisor o PIN incorrecto.» y el bloqueo
 * `data.error = POS_PIN_LOCKED` (#998) son errores de la autorización ya enviada y la pantalla los muestra.
 * El backend no emite un código estable para el pedido, de ahí el texto; el código sí descarta el bloqueo.
 */
export function isSupervisorRequiredError(err: {
  status?: number;
  error?: { message?: string; data?: { error?: string } | null } | null;
}): boolean {
  if (err?.status !== 400) return false;
  if (err.error?.data?.error === 'POS_PIN_LOCKED') return false;
  return SUPERVISOR_REQUIRED_TEXT.test(err.error?.message ?? '');
}

/**
 * Detecta el error de "sesión no operable". El backend NO usa 423: una sesión Locked/Closed/inexistente
 * al operar devuelve 400 con el mensaje "Sesión POS no válida o no está abierta" (confirmado por backend
 * 2026-05-24). No hay discriminador en el cuerpo, por lo que el FE debe consultar la sesión actual para
 * decidir el destino (Locked → PIN; cerrada/inexistente → selección de caja).
 */
export function isSessionNotOpenError(err: { status?: number; error?: { message?: string } | null }): boolean {
  return err?.status === 400 && /no est[aá] abierta/i.test(err?.error?.message ?? '');
}

/**
 * Endpoints POS donde el 404 es un estado de negocio válido (no un error).
 * El backend retorna 404 cuando no hay sesión activa o no se pudo resolver una lista de precios.
 * Para estos casos cortamos la cadena antes del error-handler global para evitar ruido en consola / Rollbar.
 */
const POS_404_AS_EMPTY_PATTERNS: RegExp[] = [
  /\/api\/v1\/pos\/sessions\/register\/[^/]+\/current/i,
  /\/api\/v1\/pos\/registers\/[^/]+\/effective-price-list/i,
];

export const posErrorInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.includes('/api/v1/pos/')) {
    return next(req);
  }

  const router = inject(Router);
  const posContext = inject(PosContextService);
  const sessions = inject(PosSessionsApiService);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 404 && POS_404_AS_EMPTY_PATTERNS.some((p) => p.test(req.url))) {
        // 404 esperado: tratarlo como respuesta vacía exitosa para no contaminar logs/Rollbar.
        return of(new HttpResponse({ status: 200, body: { data: null } }));
      }

      // Sesión no operable (Locked/Closed/inexistente): el backend devuelve 400 con un mensaje genérico.
      // Se consulta la sesión actual para decidir el destino. Se excluye el propio endpoint `current`.
      if (isSessionNotOpenError(err) && !/\/sessions\/register\/[^/]+\/current/i.test(req.url)) {
        handleSessionNotOpen(sessions, posContext, router);
        return throwError(() => err);
      }

      // Errores de permiso/negocio que requieren autorización de supervisor.
      // Tras la refactorización (guía v3.0 §3.2/§3.6) llegan como 400 con un mensaje
      // que implica supervisor (antes el FE los detectaba en 500).
      if (isSupervisorRequiredError(err)) {
        POS_SUPERVISOR_REQUIRED$.next(true);
        return throwError(() => err);
      }

      return throwError(() => err);
    })
  );
};

/**
 * Resuelve el destino tras un "sesión no operable": consulta la sesión actual de la caja y redirige.
 * Locked → pantalla de PIN; cerrada/inexistente → selección de caja.
 */
function handleSessionNotOpen(sessions: PosSessionsApiService, posContext: PosContextService, router: Router): void {
  const ctx = posContext.context();
  if (!ctx) {
    router.navigate(['/pos/select-register']);
    return;
  }
  sessions
    .apiV1PosSessionsRegisterRegisterIdCurrentGet({ registerId: ctx.registerId, tenantId: ctx.tenantId })
    .subscribe({
      next: (res) => {
        const status = (res as { data?: { status?: PosSessionStatus } } | null)?.data?.status;
        if (status === PosSessionStatus.Locked) {
          posContext.updateStatus(PosSessionStatus.Locked);
          router.navigate(['/pos/pin']);
        } else if (!status) {
          // Sesión cerrada o inexistente (el 404 de `current` se mapea a { data: null }).
          router.navigate(['/pos/select-register']);
        }
        // status Open: condición de carrera transitoria; no se navega.
      },
      error: () => router.navigate(['/pos/select-register']),
    });
}
